import type { HighlightedAttributesSectionModel } from "../types";

import HighlightedAttributesTooltip from "./HighlightedAttributesTooltip";
import { styles } from "./styles";
import { Flex, Box } from "../../../../../../../../mads";
import { Text } from "../../../../../../../../mads/Text";
import { type GettextInterface, useI18n } from "../../../../../../../../shared/I18n";
import { useModalContext } from "../../../context/JMUIModalContextProvider";
import { SegmentationSection } from "../utils/usePreferenceSections";

import React, { type ReactElement } from "react";

// Manual overrides to handle any mismatches between HighlightedAttributes and the rest of Job Details
const getTitleOverrides = (gt: GettextInterface) => {
  return {
    [SegmentationSection.MEDICAL_SPECIALTY]: "Specialty",
    [gt.gettext("Shift and schedule")]: "Available shifts",
  };
};

const getSectionsToHide = (gt: GettextInterface, tileSectionTitles: string[]): Set<string> => {
  const sectionsToHide = new Set<string>();
  const overrides = getTitleOverrides(gt);

  tileSectionTitles.forEach((title) => sectionsToHide.add(overrides[title] ?? title));

  return sectionsToHide;
};

// Separate component for rendering attribute items
const AttributeItem: React.FC<{
  attr: string;
  tooltip: ReactElement;
  isBulletList?: boolean;
}> = ({ attr, tooltip, isBulletList }) => {
  if (isBulletList) {
    return (
      <Box sx={{ paddingLeft: 16, flexDirection: "row", gap: 12 }}>
        <Flex sx={{ flexDirection: "row", alignItems: "center" }}>
          <Text sx={{ fontSize: 18, paddingRight: 10 }}>{"\u2022"}</Text>
          <Text>{attr}</Text>
          {tooltip}
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      <Text>{attr}</Text>
      {tooltip}
    </Box>
  );
};

export const HighlightedAttributes: React.FC<{
  highlightedAttributesSectionModels?: HighlightedAttributesSectionModel[];
  tileSectionTitles: string[];
}> = ({ highlightedAttributesSectionModels, tileSectionTitles }) => {
  const gt = useI18n();
  const { setAttributeTooltipModalProps, dismissAttributeTooltipModal } = useModalContext();
  const sectionsToHide = getSectionsToHide(gt, tileSectionTitles);
  const sections = (highlightedAttributesSectionModels || []).filter(
    (section) => !sectionsToHide.has(section.label),
  );

  return (
    <>
      {sections?.map((model) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        let hashedTooltips = {} as any;
        if (model.tooltips) {
          /* (attribute_id -> tooltip) */

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          hashedTooltips = model.tooltips.reduce((hash: any, tooltip: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (tooltip.attribute) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              hash[tooltip.attribute] = tooltip;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return hash;
          }, {});
        }

        // Check if this is an "Encouraged to Apply" section

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const isEncouragedToApply = Boolean(hashedTooltips.Q5R8A);

        const tooltips = model.highlightedAttributes
          .map((attr, idx, attributes): [string, ReactElement] | null => {
            const shouldAddSpace = idx < attributes.length - 1 && !isEncouragedToApply;
            const id = model.highlightedAttributesIds?.[idx];

            if (
              id &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              hashedTooltips[id]?.tooltipActive &&
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              hashedTooltips[id]?.tooltipText
            ) {
              return [
                attr,
                <HighlightedAttributesTooltip
                  key={attr}
                  label={attr}
                  onPress={() => {
                    setAttributeTooltipModalProps({
                      title: attr,

                      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      content: <Text>{hashedTooltips[id].tooltipText}</Text>,
                      isOpen: true,
                      onDismiss: dismissAttributeTooltipModal,
                      labelClose: "close",
                    });
                  }}
                  shouldAddSpace={shouldAddSpace}
                />,
              ];
            }
            return null;
          })
          .filter((tooltip): tooltip is [string, ReactElement] => tooltip !== null);

        if (tooltips.length === 0) {
          return null;
        }

        return (
          <Box
            sx={{
              ...styles.sectionContentPreferenceMatch,
              ...styles.highlightedAttributes,
            }}
            key={`highlightedAttributesSection-${model.label}`}
          >
            <Text sx={styles.sectionItemKey}>{model.label}</Text>
            <Box>
              {tooltips.map(([attr, tooltip], idx) => (
                <AttributeItem
                  key={`${model.label}-${idx}`}
                  attr={attr}
                  tooltip={tooltip}
                  isBulletList={isEncouragedToApply}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </>
  );
};
